import React, { useEffect } from 'react';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
export function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy & Communication Policy | BRIXEN CREDIT LLC';
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900" data-id="element-808">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-809">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-810">
          <a href="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 font-medium transition-colors" data-id="element-811">
            <ArrowLeft className="w-5 h-5 mr-2" data-id="element-812" />
            Back to Home
          </a>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-813">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12" data-id="element-814">
          {/* Page Title */}
          <div className="border-b border-gray-100 pb-8 mb-8" data-id="element-815">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-816">
              Privacy & Communication Policy
            </h1>
            <p className="text-sm text-gray-500 font-medium" data-id="element-817">
              Last Updated: January 30, 2026
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12" data-id="element-818">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-819">
              Table of Contents
            </h2>
            <nav data-id="element-820">
              <ul className="space-y-2 text-sm md:text-base" data-id="element-821">
                {[{
                id: 'introduction',
                label: '1. Introduction'
              }, {
                id: 'personal-info',
                label: '2. Personal Information We Collect'
              }, {
                id: 'purpose',
                label: '3. Purpose of Data Collection'
              }, {
                id: 'sharing',
                label: '4. Information Sharing & Disclosure'
              }, {
                id: 'security',
                label: '5. Data Security & Storage'
              }, {
                id: 'communication',
                label: '6. Multichannel Communication Consent'
              }, {
                id: 'responsibilities',
                label: '7. Client Responsibilities'
              }, {
                id: 'termination',
                label: '8. Right to Terminate Services'
              }, {
                id: 'updates',
                label: '9. Policy Updates'
              }, {
                id: 'contact',
                label: '10. Contact Information'
              }].map(item => <li key={item.id} data-id="element-822">
                    <a href={`#${item.id}`} onClick={e => scrollToSection(e, item.id)} className="text-purple-600 hover:text-purple-800 hover:underline transition-colors" data-id="element-823">
                      {item.label}
                    </a>
                  </li>)}
              </ul>
            </nav>
          </div>

          {/* Content Sections */}
          <div className="prose prose-lg max-w-none text-gray-700 space-y-12" data-id="element-824">
            <section id="introduction" className="scroll-mt-24" data-id="element-825">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-826">
                1. Introduction
              </h2>
              <p className="mb-4" data-id="element-827">
                BRIXEN CREDIT LLC ("Company," "we," "us," or "our") is committed
                to protecting the privacy, confidentiality, and security of your
                personal and financial information. This Privacy & Communication
                Policy explains in detail how we collect, use, store, safeguard,
                and disclose personal data when you visit our website, request
                information, submit forms, schedule consultations, enroll in our
                services, or communicate with us through any channel.
              </p>
              <p className="mb-4" data-id="element-828">
                By accessing our website, submitting personal information,
                authorizing communications, or enrolling in any program, you
                acknowledge, understand, and agree to the practices described in
                this Policy. If you do not agree, you should not use our website
                or services.
              </p>
              <p data-id="element-829">
                This Policy operates in conjunction with our Terms of Service
                and Refund & Cancellation Policy.
              </p>
            </section>

            <section id="personal-info" className="scroll-mt-24" data-id="element-830">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-831">
                2. Personal Information We Collect
              </h2>
              <p className="mb-4" data-id="element-832">
                To properly perform credit restoration, compliance, and
                consulting services, the Company may collect, store, and process
                Personally Identifiable Information (PII) and sensitive personal
                data, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-833">
                <li data-id="element-834">Full legal name</li>
                <li data-id="element-835">Social Security Number (SSN)</li>
                <li data-id="element-836">Date of birth</li>
                <li data-id="element-837">Residential and mailing address</li>
                <li data-id="element-838">Telephone number(s) and email address</li>
                <li data-id="element-839">Credit reports and credit monitoring credentials</li>
                <li data-id="element-840">Financial and payment information</li>
                <li data-id="element-841">Government-issued documentation when required</li>
                <li data-id="element-842">
                  Any documentation, correspondence, or records provided by the
                  Client related to credit accounts, disputes, or identity
                  verification
                </li>
              </ul>
              <p className="mb-4" data-id="element-843">
                This information is collected solely for legitimate business
                purposes, including execution of authorized services, regulatory
                compliance, identity verification, billing, fraud prevention,
                and fulfillment of federal reporting obligations.
              </p>
              <p data-id="element-844">
                The Company does not knowingly collect information from
                individuals under the age of 18.
              </p>
            </section>

            <section id="purpose" className="scroll-mt-24" data-id="element-845">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-846">
                3. Purpose of Data Collection
              </h2>
              <p className="mb-4" data-id="element-847">
                The Client's personal data is used strictly and exclusively for
                the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-848">
                <li data-id="element-849">Conducting credit audits, reviews, and analysis</li>
                <li data-id="element-850">
                  Drafting, managing, submitting, and tracking dispute letters
                  and reinvestigation requests
                </li>
                <li data-id="element-851">
                  Communicating with credit bureaus, creditors, debt collectors,
                  and data furnishers
                </li>
                <li data-id="element-852">
                  Managing the Client's file within secure internal software
                  platforms
                </li>
                <li data-id="element-853">
                  Filing authorized reports or complaints with federal agencies,
                  including the FTC and CFPB, when applicable
                </li>
                <li data-id="element-854">
                  Providing account updates, service notifications, reminders,
                  and educational or informational content
                </li>
                <li data-id="element-855">
                  Processing payments, billing, compliance reviews, fraud
                  prevention, and internal record-keeping
                </li>
              </ul>
              <p data-id="element-856">
                The Company does not sell, rent, lease, or trade Client data to
                third parties for marketing or advertising purposes under any
                circumstances.
              </p>
            </section>

            <section id="sharing" className="scroll-mt-24" data-id="element-857">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-858">
                4. Information Sharing & Disclosure
              </h2>
              <p className="mb-4" data-id="element-859">
                The Company may share Client information only when strictly
                necessary and only with trusted third parties for legitimate
                service-related or legal purposes, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-860">
                <li data-id="element-861">Credit bureaus: Equifax, Experian, TransUnion</li>
                <li data-id="element-862">
                  Secondary or specialty bureaus: LexisNexis, Innovis,
                  ChexSystems, and similar agencies
                </li>
                <li data-id="element-863">Credit monitoring services authorized by the Client</li>
                <li data-id="element-864">
                  Federal agencies such as the Federal Trade Commission (FTC)
                  and Consumer Financial Protection Bureau (CFPB) when
                  submitting complaints, escalations, or identity theft reports
                </li>
                <li data-id="element-865">
                  Legal authorities, courts, regulators, or law enforcement when
                  required by law, subpoena, court order, or regulatory request
                </li>
              </ul>
              <p data-id="element-866">
                All third parties receiving Client data are required to handle
                such data in compliance with applicable privacy, security, and
                confidentiality laws.
              </p>
            </section>

            <section id="security" className="scroll-mt-24" data-id="element-867">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-868">
                5. Data Security & Storage
              </h2>
              <p className="mb-4" data-id="element-869">
                The Company implements administrative, technical, and physical
                safeguards designed to protect Client information against
                unauthorized access, misuse, disclosure, alteration, or
                destruction. These measures may include secure servers,
                encrypted data storage, access controls, and internal compliance
                procedures.
              </p>
              <p className="mb-4" data-id="element-870">
                However, no method of data transmission or electronic storage is
                100% secure. By using our services, the Client acknowledges and
                accepts the inherent risks associated with internet-based data
                transmission.
              </p>
              <p data-id="element-871">
                The Company disclaims liability for unauthorized access beyond
                its reasonable control.
              </p>
            </section>

            <section id="communication" className="scroll-mt-24" data-id="element-872">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-873">
                6. Multichannel Communication Consent (TCPA & TSR Compliance)
              </h2>
              <p className="mb-4" data-id="element-874">
                By submitting your information, requesting services, or
                enrolling in any program, you provide Express Written Consent
                authorizing BRIXEN CREDIT LLC and its agents, representatives,
                or service providers to contact you through multiple
                communication channels, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-875">
                <li data-id="element-876">
                  Telephone calls, including calls made using automated dialing
                  systems or prerecorded messages
                </li>
                <li data-id="element-877">
                  Text messages and WhatsApp, for case updates, document
                  requests, reminders, confirmations, and customer support
                </li>
                <li data-id="element-878">
                  Email communications, including transactional emails, account
                  notices, educational materials, and service-related updates
                </li>
              </ul>
              <p className="mb-4" data-id="element-879">
                This consent is provided in compliance with the Telephone
                Consumer Protection Act (TCPA) and the Telemarketing Sales Rule
                (TSR).
              </p>
              <p className="mb-4" data-id="element-880">
                Consent is not a condition of purchase and may be revoked at any
                time by:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-881">
                <li data-id="element-882">
                  Emailing:{' '}
                  <a href="mailto:soporte@brixencredit.com" className="text-purple-600 hover:underline" data-id="element-883">
                    soporte@brixencredit.com
                  </a>
                </li>
                <li data-id="element-884">Replying "STOP" to any SMS or messaging communication</li>
              </ul>
              <p data-id="element-885">
                Standard message and data rates may apply depending on the
                Client's carrier.
              </p>
            </section>

            <section id="responsibilities" className="scroll-mt-24" data-id="element-886">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-887">
                7. Client Responsibilities
              </h2>
              <p className="mb-4" data-id="element-888">The Client is responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-889">
                <li data-id="element-890">Providing accurate, truthful, and complete information</li>
                <li data-id="element-891">
                  Promptly updating the Company if personal or contact
                  information changes
                </li>
                <li data-id="element-892">
                  Safeguarding login credentials and access to third-party
                  monitoring services
                </li>
              </ul>
              <p data-id="element-893">
                Providing false, misleading, or incomplete information may
                result in immediate service suspension or termination, and may
                expose the Client to legal liability in cases involving federal
                reporting.
              </p>
            </section>

            <section id="termination" className="scroll-mt-24" data-id="element-894">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-895">
                8. Right to Terminate Services
              </h2>
              <p className="mb-4" data-id="element-896">
                The Company reserves the right, at its sole discretion, to
                suspend or terminate services without notice due to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-897">
                <li data-id="element-898">Non-payment or failed billing</li>
                <li data-id="element-899">Lack of cooperation or repeated non-compliance</li>
                <li data-id="element-900">Fraudulent activity or misrepresentation</li>
                <li data-id="element-901">
                  Violation of this Policy, the Terms of Service, or any related
                  agreement
                </li>
              </ul>
              <p data-id="element-902">
                Termination does not relieve the Client of any outstanding
                financial obligations.
              </p>
            </section>

            <section id="updates" className="scroll-mt-24" data-id="element-903">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-904">
                9. Policy Updates
              </h2>
              <p data-id="element-905">
                The Company reserves the right to modify or update this Privacy
                & Communication Policy at any time. Continued use of the website
                or services after updates are posted constitutes acceptance of
                the revised Policy.
              </p>
            </section>

            <section id="contact" className="scroll-mt-24" data-id="element-906">
              <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-907">
                10. Contact Information
              </h2>

              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-908">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-909">
                  BRIXEN CREDIT LLC
                </h3>
                <div className="space-y-4" data-id="element-910">
                  <div className="flex items-start" data-id="element-911">
                    <Phone className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-912" />
                    <div data-id="element-913">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-914">
                        Phone
                      </span>
                      <a href="tel:786-738-6588" className="text-lg text-gray-900 hover:text-purple-600 transition-colors" data-id="element-915">
                        786-738-6588
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-916">
                    <Mail className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-917" />
                    <div data-id="element-918">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-919">
                        Email
                      </span>
                      <a href="mailto:soporte@brixencredit.com" className="text-lg text-gray-900 hover:text-purple-600 transition-colors" data-id="element-920">
                        soporte@brixencredit.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-921">
                    <MapPin className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-922" />
                    <div data-id="element-923">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-924">
                        Address
                      </span>
                      <span className="text-lg text-gray-900 block" data-id="element-925">
                        390 NE 191ST ST, MIAMI, FL 33179
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>;
}